import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Plus, Clock, Cake, Flame, UserPlus, Target,
  CalendarX, Trash2, Play, Pause, TestTube, X,
  Save, ChevronDown, ChevronUp, History
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TRIGGER_ICONS = {
  inactivity: Clock,
  birthday: Cake,
  streak_lost: Flame,
  new_customer: UserPlus,
  goal_reached: Target,
  no_booking: CalendarX,
};

const TRIGGER_COLORS = {
  inactivity: 'bg-orange-50 text-orange-700 border-orange-200',
  birthday: 'bg-pink-50 text-pink-700 border-pink-200',
  streak_lost: 'bg-red-50 text-red-700 border-red-200',
  new_customer: 'bg-blue-50 text-blue-700 border-blue-200',
  goal_reached: 'bg-green-50 text-green-700 border-green-200',
  no_booking: 'bg-purple-50 text-purple-700 border-purple-200',
};

export const PushAutomations = () => {
  const [triggers, setTriggers] = useState([]);
  const [rules, setRules] = useState([]);
  const [history, setHistory] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [testing, setTesting] = useState(null);
  const [testResult, setTestResult] = useState(null);

  // Form state
  const [form, setForm] = useState({
    trigger_type: '', title: '', message: '',
    params: {}, target_platform: 'all', active: true,
    quiet_start: '22:00', quiet_end: '08:00',
  });

  const headers = useCallback(() => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
  }), []);

  const fetchData = useCallback(async () => {
    try {
      const [trigRes, rulesRes, histRes] = await Promise.all([
        axios.get(`${API}/admin/push-automations/triggers`, { headers: headers() }),
        axios.get(`${API}/admin/push-automations/rules`, { headers: headers() }),
        axios.get(`${API}/admin/push-automations/history?limit=15`, { headers: headers() }),
      ]);
      setTriggers(trigRes.data);
      setRules(rulesRes.data);
      setHistory(histRes.data);
    } catch (err) {
      console.error('Automations fetch error:', err);
    }
  }, [headers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async () => {
    if (!form.trigger_type || !form.title || !form.message) {
      toast.error('Bitte alle Pflichtfelder ausfüllen');
      return;
    }
    try {
      await axios.post(`${API}/admin/push-automations/rules`, form, { headers: headers() });
      toast.success('Automatisierung erstellt');
      setShowCreate(false);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Erstellen');
    }
  };

  const handleToggle = async (ruleId) => {
    try {
      const res = await axios.post(`${API}/admin/push-automations/rules/${ruleId}/toggle`, {}, { headers: headers() });
      toast.success(res.data.active ? 'Aktiviert' : 'Deaktiviert');
      fetchData();
    } catch (err) {
      toast.error('Fehler beim Umschalten');
    }
  };

  const handleDelete = async (ruleId) => {
    try {
      await axios.delete(`${API}/admin/push-automations/rules/${ruleId}`, { headers: headers() });
      toast.success('Automatisierung gelöscht');
      fetchData();
    } catch (err) {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleTest = async (ruleId) => {
    setTesting(ruleId);
    setTestResult(null);
    try {
      const res = await axios.post(`${API}/admin/push-automations/rules/${ruleId}/test`, {}, { headers: headers() });
      setTestResult(res.data);
    } catch (err) {
      toast.error('Fehler beim Testen');
    } finally {
      setTesting(null);
    }
  };

  const resetForm = () => {
    setForm({
      trigger_type: '', title: '', message: '',
      params: {}, target_platform: 'all', active: true,
      quiet_start: '22:00', quiet_end: '08:00',
    });
    setTestResult(null);
  };

  const selectTrigger = (triggerId) => {
    const trigger = triggers.find(t => t.id === triggerId);
    const defaultParams = {};
    if (trigger) {
      trigger.params.forEach(p => { defaultParams[p.key] = p.default; });
    }
    setForm(prev => ({ ...prev, trigger_type: triggerId, params: defaultParams }));
  };

  const selectedTrigger = triggers.find(t => t.id === form.trigger_type);

  return (
    <div className="space-y-4" data-testid="push-automations-section">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2" data-testid="automations-title">
                <Play className="w-5 h-5 text-amber-600" />
                Push-Automatisierungen
              </CardTitle>
              <CardDescription>
                Automatische Push-Benachrichtigungen basierend auf Kundenverhalten
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-xs" data-testid="automations-count">
                {rules.filter(r => r.active).length}/{rules.length} aktiv
              </Badge>
              <Button
                onClick={() => { setShowCreate(!showCreate); if (showCreate) resetForm(); }}
                size="sm"
                data-testid="create-automation-btn"
                className={showCreate ? 'bg-gray-200 text-gray-700 hover:bg-gray-300' : 'bg-amber-600 hover:bg-amber-700'}
              >
                {showCreate ? <X className="w-4 h-4 mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
                {showCreate ? 'Abbrechen' : 'Neue Regel'}
              </Button>
            </div>
          </div>
        </CardHeader>

        {/* Create Form */}
        {showCreate && (
          <CardContent className="border-t pt-4 space-y-4" data-testid="create-automation-form">
            {/* Trigger Selection */}
            <div>
              <Label className="mb-2 block">Auslöser *</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {triggers.map(t => {
                  const Icon = TRIGGER_ICONS[t.id] || Clock;
                  const isSelected = form.trigger_type === t.id;
                  return (
                    <button
                      key={t.id}
                      onClick={() => selectTrigger(t.id)}
                      data-testid={`trigger-${t.id}`}
                      className={`flex items-center gap-2 p-3 rounded-lg border text-left text-sm transition-all ${
                        isSelected
                          ? `${TRIGGER_COLORS[t.id]} border-2 shadow-sm`
                          : 'border-gray-200 hover:border-gray-300 text-gray-600'
                      }`}
                    >
                      <Icon className="w-4 h-4 shrink-0" />
                      <div>
                        <div className="font-medium text-xs">{t.name}</div>
                        <div className="text-[10px] opacity-70">{t.description}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Parameters */}
            {selectedTrigger?.params?.length > 0 && (
              <div className="flex flex-wrap gap-4">
                {selectedTrigger.params.map(p => (
                  <div key={p.key} className="space-y-1">
                    <Label className="text-xs">{p.label}</Label>
                    <Input
                      type="number"
                      min={p.min} max={p.max}
                      value={form.params[p.key] || p.default}
                      onChange={e => setForm(prev => ({
                        ...prev, params: { ...prev.params, [p.key]: parseInt(e.target.value) || p.default }
                      }))}
                      className="w-24"
                      data-testid={`param-${p.key}`}
                    />
                  </div>
                ))}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Titel * <span className="text-gray-400 text-xs">Platzhalter: {selectedTrigger?.placeholders?.join(', ') || '{name}'}</span></Label>
                <Input
                  value={form.title}
                  onChange={e => setForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="z.B. Hey {name}, wir vermissen dich!"
                  data-testid="automation-title-input"
                />
              </div>
              <div className="space-y-1">
                <Label>Nachricht *</Label>
                <Input
                  value={form.message}
                  onChange={e => setForm(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="z.B. Komm wieder ins Training!"
                  data-testid="automation-message-input"
                />
              </div>
            </div>

            <div className="flex flex-wrap items-end gap-4">
              <div className="space-y-1">
                <Label className="text-xs">Zielgruppe</Label>
                <div className="flex gap-1">
                  {['all', 'ios', 'android'].map(p => (
                    <button key={p} onClick={() => setForm(prev => ({ ...prev, target_platform: p }))}
                      data-testid={`auto-target-${p}`}
                      className={`px-3 py-1.5 text-xs rounded border transition ${
                        form.target_platform === p ? 'bg-amber-50 border-amber-400 text-amber-700 font-medium' : 'border-gray-200 text-gray-500'
                      }`}>
                      {p === 'all' ? 'Alle' : p.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Ruhezeit (kein Versand)</Label>
                <div className="flex items-center gap-1 text-xs">
                  <Input type="time" value={form.quiet_start}
                    onChange={e => setForm(prev => ({ ...prev, quiet_start: e.target.value }))}
                    className="w-28 text-xs" data-testid="quiet-start" />
                  <span className="text-gray-400">bis</span>
                  <Input type="time" value={form.quiet_end}
                    onChange={e => setForm(prev => ({ ...prev, quiet_end: e.target.value }))}
                    className="w-28 text-xs" data-testid="quiet-end" />
                </div>
              </div>
              <Button onClick={handleCreate} className="bg-amber-600 hover:bg-amber-700" data-testid="save-automation-btn">
                <Save className="w-4 h-4 mr-1" /> Erstellen
              </Button>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Rules List */}
      {rules.length > 0 && (
        <div className="space-y-2">
          {rules.map(rule => {
            const Icon = TRIGGER_ICONS[rule.trigger_type] || Clock;
            const colors = TRIGGER_COLORS[rule.trigger_type] || 'bg-gray-50 text-gray-700 border-gray-200';
            return (
              <Card key={rule.id} className={`transition-opacity ${!rule.active ? 'opacity-50' : ''}`} data-testid={`rule-${rule.id}`}>
                <CardContent className="py-3 px-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 border ${colors}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium truncate">{rule.title}</span>
                        <Badge variant="outline" className="text-[10px] shrink-0">{
                          triggers.find(t => t.id === rule.trigger_type)?.name || rule.trigger_type
                        }</Badge>
                        {rule.target_platform !== 'all' && (
                          <Badge variant="outline" className="text-[10px]">{rule.target_platform}</Badge>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 truncate mt-0.5">{rule.message}</p>
                      <div className="flex items-center gap-3 mt-1 text-[10px] text-gray-400">
                        {rule.last_run && <span>Letzter Lauf: {new Date(rule.last_run).toLocaleString('de-CH')}</span>}
                        <span>Gesendet: {rule.total_sent || 0}</span>
                        <span>Ruhezeit: {rule.quiet_start}–{rule.quiet_end}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button variant="ghost" size="sm"
                        onClick={() => handleTest(rule.id)}
                        disabled={testing === rule.id}
                        data-testid={`test-rule-${rule.id}`}
                        title="Testen">
                        <TestTube className={`w-3.5 h-3.5 ${testing === rule.id ? 'animate-spin' : ''}`} />
                      </Button>
                      <Switch
                        checked={rule.active}
                        onCheckedChange={() => handleToggle(rule.id)}
                        data-testid={`toggle-rule-${rule.id}`}
                      />
                      <Button variant="ghost" size="sm"
                        onClick={() => handleDelete(rule.id)}
                        data-testid={`delete-rule-${rule.id}`}
                        className="text-red-400 hover:text-red-600">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                  {/* Test Result */}
                  {testResult && testResult.rule_id === rule.id && (
                    <div className="mt-2 p-2 bg-blue-50 rounded text-xs border border-blue-200" data-testid="test-result">
                      <span className="font-medium text-blue-700">{testResult.matching_customers} Kunden</span>
                      <span className="text-blue-500"> würden diese Nachricht erhalten</span>
                      {testResult.sample?.length > 0 && (
                        <span className="text-blue-400 ml-1">
                          (z.B. {testResult.sample.map(s => s.name).join(', ')})
                        </span>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {rules.length === 0 && !showCreate && (
        <Card>
          <CardContent className="py-10 text-center text-gray-400">
            <Play className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">Noch keine Automatisierungen erstellt.</p>
            <p className="text-xs mt-1">Klicken Sie auf "Neue Regel" um zu starten.</p>
          </CardContent>
        </Card>
      )}

      {/* Automation History */}
      {history.length > 0 && (
        <Card data-testid="automation-history-card">
          <CardHeader>
            <button className="flex items-center justify-between w-full"
              onClick={() => setShowHistory(!showHistory)} data-testid="automation-history-toggle">
              <CardTitle className="flex items-center gap-2 text-base">
                <History className="w-4 h-4 text-gray-500" />
                Ausführungs-Verlauf ({history.length})
              </CardTitle>
              {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          </CardHeader>
          {showHistory && (
            <CardContent>
              <div className="space-y-1">
                {history.map(h => (
                  <div key={h.id} className="flex items-center justify-between p-2 border rounded text-xs bg-gray-50">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-gray-400 shrink-0">
                        {h.executed_at ? new Date(h.executed_at).toLocaleString('de-CH') : ''}
                      </span>
                      <span className="font-medium truncate">{h.rule_name}</span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-gray-500">{h.matching_customers} Kunden</span>
                      <span className="text-gray-500">{h.tokens_targeted} Tokens</span>
                      {h.fcm_available ? (
                        <Badge className="bg-green-100 text-green-700 text-[10px]">{h.fcm_success} zugestellt</Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px]">FCM inaktiv</Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          )}
        </Card>
      )}
    </div>
  );
};
